import classNames from 'classnames';
import type { ComponentProps } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { So5FixtureEvent } from '__generated__/globalTypes';
import { Projection } from 'atoms/Projection';
import { RotowireSmallLogo } from 'atoms/icons/RotowireLogo';
import { Tooltip } from 'atoms/tooltip/Tooltip';
import { LabelS } from 'atoms/typography';
import { PoweredByRotowire } from 'components/rotowire/PoweredByRotowire';
import { scoutingCenter } from 'lib/glossary';

const AUTHORIZED_SO5_FIXTURE_EVENTS: So5FixtureEvent[] = [
  So5FixtureEvent.DAILY,
  So5FixtureEvent.CLASSIC,
];

const Label = styled.span`
  font: var(--t-label-m);
  font-family: var(--brand-font);
  &.small {
    font: var(--t-label-s);
    font-family: var(--brand-font);
  }
`;

type Props = {
  size: ComponentProps<typeof Projection>['size'];
  projectedScore: number | null;
  so5FixtureEvent: So5FixtureEvent;
  withTooltip?: boolean;
  withLogo?: boolean;
};

const tooltipMessages: { [k in So5FixtureEvent]?: MessageDescriptor } = {
  [So5FixtureEvent.DAILY]: scoutingCenter.projectionDailyTooltip,
  [So5FixtureEvent.CLASSIC]: scoutingCenter.projectionClassicTooltip,
};

export const ProjectionScore = ({
  size,
  projectedScore,
  so5FixtureEvent,
  withTooltip,
  withLogo,
}: Props) => {
  if (
    !AUTHORIZED_SO5_FIXTURE_EVENTS.includes(so5FixtureEvent) ||
    !projectedScore
  )
    return null;

  const small = size <= 3;
  const logoSize = size > 3 ? 1.5 : 1;

  const projectionScore = (
    <Projection
      size={size}
      color="var(--c-rotowire)"
      inverted={so5FixtureEvent === So5FixtureEvent.DAILY}
    >
      {!!withLogo && <RotowireSmallLogo size={logoSize} />}
      <Label
        className={classNames({
          small,
        })}
        color="var(--c-white)"
      >
        <FormattedNumber value={projectedScore} maximumFractionDigits={0} />
      </Label>
    </Projection>
  );
  if (withTooltip) {
    return (
      <Tooltip
        title={
          <LabelS className="text-center">
            <FormattedMessage
              {...tooltipMessages[so5FixtureEvent]}
              values={{ rotowireLogo: <PoweredByRotowire /> }}
            />
          </LabelS>
        }
      >
        {projectionScore}
      </Tooltip>
    );
  }
  return projectionScore;
};
